/**
 * Data Loader for Portfolio
 * Fetches JSON configuration files and merges with manifest data
 */

import manifest from './manifest.json';

const CONTENT_BASE = '/content';

// Cache for loaded data
const dataCache = {};

/**
 * Loads a JSON file from the content directory
 */
async function loadJSON(path) {
  const cacheKey = `json:${path}`;
  if (dataCache[cacheKey]) return dataCache[cacheKey];

  try {
    const response = await fetch(`${CONTENT_BASE}/${path}`);
    if (!response.ok) throw new Error(`Failed to load ${path}`);
    const data = await response.json();
    dataCache[cacheKey] = data;
    return data;
  } catch (error) {
    console.error(`Error loading ${path}:`, error);
    return null;
  }
}

/**
 * Loads the profile data (personal info, stats, featured projects)
 */
export async function loadProfile() {
  return loadJSON('profile.json');
}

/**
 * Loads the timeline (career history)
 */
export async function loadTimeline() {
  const data = await loadJSON('timeline.json');
  return data?.positions || [];
}

/**
 * Loads skills and certificates
 */
export async function loadSkills() {
  return loadJSON('skills.json');
}

/**
 * Loads client logos
 */
export async function loadClients() {
  const data = await loadJSON('clients.json');
  return data?.clients || [];
}

/**
 * Loads tech innovations
 */
export async function loadInnovations() {
  const data = await loadJSON('innovations.json');
  return data?.innovations || [];
}

/**
 * Loads additional (non-featured) projects
 */
export async function loadAdditionalProjects() {
  const data = await loadJSON('additional-projects.json');
  return data?.projects || [];
}

/**
 * Gets a single project by slug from the manifest
 */
export function getProject(slug) {
  return manifest.projects[slug] || null;
}

/**
 * Gets all projects from the manifest
 */
export function getAllProjects() {
  return Object.values(manifest.projects).sort((a, b) => {
    // Sort by year descending (newest first)
    return b.year.localeCompare(a.year);
  });
}

/**
 * Gets featured projects based on profile.featuredProjects slugs
 */
export async function getFeaturedProjects() {
  const profile = await loadProfile();
  if (!profile?.featuredProjects) return [];

  return profile.featuredProjects
    .map(slug => getProject(slug))
    .filter(Boolean);
}

/**
 * Gets the project stats from profile
 */
export async function getStats() {
  const profile = await loadProfile();
  return profile?.stats || {};
}

// Export manifest for direct access if needed
export { manifest };
